import { formatNotReportingReason } from "encamp-shared/src/submissions/notReportingReasonLabels";
import { SubmissionStatus, SubmissionStepType } from "generated-graphql/graphql";
import { SubmissionReviewState } from "hooks/submission/reviewStates";
import { STATUS_LABELS } from "utils/submissionStatusLabels";

export type DocumentStepBanner = {
  severity: "info" | "warning";
  title?: string;
  message: string;
};

export type DocumentStepStatusBannerInput = {
  status: SubmissionStatus | null | undefined;
  reviewState: SubmissionReviewState;
  isStaff: boolean;
  step: SubmissionStepType;
  notReportingReason?: string | null;
  notReportingReasonNote?: string | null;
};

// Ported verbatim from the legacy read-only view's banners
// (components/Submission/SubmissionDisplayStatic) so both surfaces narrate the
// same states in the same words.
const AWAITING_STAFF_WORK: DocumentStepBanner = {
  severity: "warning",
  title: "Submission not ready for review",
  message:
    "This submission is being worked on by Encamp Staff and the reviewer(s) and signer will be notified when it is ready for review.",
};

const REVIEWER_WAITING_FOR_TURN: DocumentStepBanner = {
  severity: "warning",
  title: "Submission is not ready for review",
  message:
    "The submission cannot be reviewed until all preceding reviews are complete. We will notify you via email once it is ready for review.",
};

// Second person, unlike the legacy wording: this only renders for the signer
// themselves, and it sits alongside the reviewer banner, which already says "you".
const SIGNER_WAITING_FOR_REVIEWERS: DocumentStepBanner = {
  severity: "warning",
  title: "Submission not ready for signature",
  message:
    "This submission cannot be signed until all preceding reviews are complete. We will notify you via email once it is ready for signing.",
};

// Names the button on this step verbatim so the instruction points somewhere.
const PROCESSING_FOR_FILING_STAFF: DocumentStepBanner = {
  severity: "info",
  message:
    "Ready to file. Once you have submitted this to the agency, select Mark as filing complete.",
};

const PROCESSING_FOR_CUSTOMER: DocumentStepBanner = {
  severity: "info",
  title: "Nothing further is needed from you",
  message:
    "Encamp staff are submitting this to the agency on your behalf. The status updates here once it has been filed.",
};

// Titles come from the legacy view's page title for a live turn; the bodies are
// new — legacy signalled the turn with that title and a tooltip, not a banner.
const READY_TO_REVIEW: DocumentStepBanner = {
  severity: "info",
  title: "Review Submission",
  message: "This submission is ready for your review on the Review & Sign step.",
};

const READY_TO_SIGN: DocumentStepBanner = {
  severity: "info",
  title: "Sign Submission",
  message: "This submission is ready for your signature on the Review & Sign step.",
};

const AWAITING_STAFF_WORK_STATUSES: SubmissionStatus[] = [
  SubmissionStatus.NotStarted,
  SubmissionStatus.InProgress,
  SubmissionStatus.EncampInitialReview,
];

/**
 * The single status banner a document-form step shows. Driven by the submission
 * status rather than by what the submission happens to require, so a submission
 * staff have not configured yet reads as "not ready" instead of as "nothing is
 * outstanding".
 */
export function pickDocumentStepStatusBanner({
  status,
  reviewState,
  isStaff,
  step,
  notReportingReason,
  notReportingReasonNote,
}: DocumentStepStatusBannerInput): DocumentStepBanner | null {
  if (!status) {
    return null;
  }

  if (status === SubmissionStatus.NotReporting) {
    // The reason carries the note when staff recorded one, so an "Other" reason
    // reads as the explanation rather than as "Other".
    const reason = formatNotReportingReason(notReportingReason, notReportingReasonNote);
    const label = STATUS_LABELS[SubmissionStatus.NotReporting];
    return {
      severity: "info",
      message: reason
        ? `This submission is marked as ${label}: ${reason}.`
        : `This submission is marked as ${label}.`,
    };
  }

  // Submit & File confirms the filing inline (MarkFilingCompleteAction) and the
  // shell relabels the flow to Filing Progress, so a banner would only repeat it.
  if (status === SubmissionStatus.FilingComplete) {
    return null;
  }

  if (status === SubmissionStatus.ProcessingSubmission) {
    if (!isStaff) {
      return PROCESSING_FOR_CUSTOMER;
    }
    // Staff only need the filing instruction, and only where the button is.
    return step === SubmissionStepType.SubmitAndFile ? PROCESSING_FOR_FILING_STAFF : null;
  }

  if (reviewState === SubmissionReviewState.SIGNER_IS_WAITING_FOR_REVIEWERS) {
    return SIGNER_WAITING_FOR_REVIEWERS;
  }
  if (reviewState === SubmissionReviewState.REVIEWER_WAITING_FOR_TURN) {
    return REVIEWER_WAITING_FOR_TURN;
  }

  // Staff are the ones doing the work the remaining banners describe, so beyond
  // their own review turn above they get the step's own affordances instead.
  if (isStaff) {
    return null;
  }

  if (AWAITING_STAFF_WORK_STATUSES.includes(status)) {
    return AWAITING_STAFF_WORK;
  }

  // Review & Sign already says this in its header, right above the review form.
  if (status !== SubmissionStatus.InReview || step === SubmissionStepType.ReviewAndSign) {
    return null;
  }
  if (reviewState === SubmissionReviewState.SIGNER_CAN_SIGN) {
    return READY_TO_SIGN;
  }
  if (reviewState === SubmissionReviewState.REVIEWER_CAN_REVIEW) {
    return READY_TO_REVIEW;
  }
  return null;
}
